import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { Loader2 } from "lucide-react";

interface OnboardingGuardProps {
  children: React.ReactNode;
}

interface OnboardingState {
  isVerified: boolean;
  hasPrivacySetup: boolean;
  profileCompletion: number;
}

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    async function checkOnboardingStatus() {
      try {
        const token = await getToken();
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const res = await fetch(`${apiUrl}/api/auth/onboarding-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          // If endpoint doesn't exist yet, allow through (graceful degradation)
          setIsChecking(false);
          return;
        }

        const status: OnboardingState = await res.json();

        // Redirect to first incomplete step
        // Note: Verification is optional - unverified users can still complete profile
        // They just won't have access to premium privacy features
        if (status.profileCompletion < 100) {
          // Redirect to appropriate profile step
          if (status.profileCompletion === 0) {
            navigate("/profile/photos", { replace: true });
          } else if (status.profileCompletion < 50) {
            navigate("/profile/bio", { replace: true });
          } else if (status.profileCompletion < 75) {
            navigate("/profile/lifestyle", { replace: true });
          } else {
            navigate("/profile/preview", { replace: true });
          }
        } else {
          // All complete - show children (Feed)
          setIsChecking(false);
        }
      } catch (error) {
        console.error("Onboarding check failed:", error);
        // Graceful degradation - allow through if API fails
        setIsChecking(false);
      }
    }

    checkOnboardingStatus();
  }, [getToken, navigate]);

  if (isChecking) {
    return (
      <div className="min-h-screen bg-[#B2FBA5] flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return <>{children}</>;
}
